/**
 * Axios service configuration
 * Centralized HTTP client with authentication and error handling
 */
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

// Create axios instance
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add auth token
axiosInstance.interceptors.request.use(
  (config) => {
    // Ne pas envoyer le token pour les routes d'authentification
    const authRoutes = ['/api/accounts/login', '/api/accounts/register', '/api/accounts/forgot-password'];
    const isAuthRoute = authRoutes.some(route => config.url?.includes(route));

    if (!isAuthRoute) {
      const token = localStorage.getItem('jwt');
      if (token) {
        // Vérifier le format du token
        const segments = token.split('.');
        if (segments.length !== 3) {
          console.error('Token invalide (pas 3 segments):', token.substring(0, 50) + '...', 'Segments:', segments.length, 'Token complet:', token);
          localStorage.removeItem('jwt');
          // Ne pas rejeter la promesse, laisser la requête continuer sans token
          // Le backend retournera 401 et l'intercepteur de réponse gérera la redirection
          return config;
        }
        config.headers.Authorization = `Bearer ${token}`;
      } else {
        console.warn('Aucun token trouvé pour la requête:', config.url);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - Handle auth errors
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response) {
      const status = error.response.status;
      const url = error.config?.url || '';

      // Handle 401 - Unauthorized (authentification requise)
      if (status === 401) {
        // Remove token
        localStorage.removeItem('jwt');

        // Redirect to login if not already there
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }

      // Handle 403 - Forbidden
      // Ne rediriger vers login que si c'est une erreur d'authentification
      // Les erreurs 403 pour clé Gemini manquante ne doivent pas rediriger
      if (status === 403) {
        const errorDetail = error.response?.data?.detail || '';
        // Si c'est une erreur d'authentification (token invalide), rediriger
        if (errorDetail.includes('Invalid token') || errorDetail.includes('credentials') || errorDetail.includes('authentication')) {
          localStorage.removeItem('jwt');
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
        // Sinon, laisser le composant gérer l'erreur (ex: clé Gemini manquante)
      }

      // Handle 404 on protected routes - might indicate auth issue
      // Routes protégées qui nécessitent une authentification
      // Ne pas rediriger automatiquement, laisser le composant gérer l'erreur
      // Un 404 peut être une vraie erreur (ressource non trouvée) et non une erreur d'auth
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;

